/* eslint-disable max-classes-per-file */
// REASON: error class hierarchy belongs together — ApiError + its three
// subclasses + the two unrelated-but-domain-similar errors live as one
// public surface so callers only import from a single module.

export interface ApiErrorInit {
  status: number;
  code: string;
  message: string;
  body?: unknown;
}

/**
 * Base class for every error thrown by `src/api`. Carries the HTTP status,
 * a stable string code for callers to branch on, and the raw body for
 * diagnostics.
 */
export class ApiError extends Error {
  public readonly status: number;

  public readonly code: string;

  public readonly body: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.body = init.body;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Thrown when an HTTP response is 401/403 or when the token endpoint
 * refuses credentials. Callers can catch this specifically to trigger
 * a re-auth flow.
 */
export class AuthError extends ApiError {
  constructor(init: { message: string; body?: unknown; status?: number; code?: string }) {
    super({
      status: init.status ?? 401,
      code: init.code ?? 'AUTH',
      message: init.message,
      body: init.body,
    });
    this.name = 'AuthError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Thrown when `fetch` itself rejects (offline, DNS failure, CORS, abort).
 * Status is 0 because no HTTP response existed.
 */
export class NetworkError extends ApiError {
  constructor(init: { message: string; body?: unknown }) {
    super({ status: 0, code: 'NETWORK', message: init.message, body: init.body });
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export interface ValidationIssue {
  path: string;
  message: string;
}

/**
 * Thrown when input validation fails. `issues` carries field-level
 * detail so UIs can highlight the offending field.
 */
export class ValidationError extends Error {
  public readonly issues: ValidationIssue[];

  constructor(init: { message: string; issues: ValidationIssue[] }) {
    super(init.message);
    this.name = 'ValidationError';
    this.issues = init.issues;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Thrown when the app cannot resolve a required configuration value
 * (e.g. no API base URL set, no active auth config).
 */
export class ConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}
